import { useState, useCallback, useEffect } from "react";


const NEWSLETTER_SENDERS = [
  "dan@tldrnewsletter.com",
  "hi@mail.theresanaiforthat.com",
  "mail@joinhorizon.ai",
  "nextoolai@mail.beehiiv.com",
  "theprohuman@mail.beehiiv.com",
  "aibreakfast@mail.beehiiv.com",
  "superhuman@mail.joinsuperhuman.ai",
  "hello@theinformation.com",
  "hello@snacks.robinhood.com",
  "newsletter@techmeme.com",
  "howiwrite@substack.com",
  "sabrinaramonov@substack.com",
  "matthew@tribedigitalco.com",
  "bensbites@substack.com",
  "d@trends.vc",
  "evolvingai@mail.beehiiv.com",
  // ── Stats, Reports & Benchmarks ──
  "newsletter@airstreet.com",          // State of AI Report (Nathan Benaich)
  "jack@importai.net",                 // Import AI (Jack Clark)
  "hai-communications@stanford.edu",   // Stanford HAI
  "newsletter@epochai.org",            // Epoch AI
  "info@cbinsights.com",               // CB Insights
  "newsletter@paperswithcode.com",     // Papers with Code
  // ── Events & Conferences ──
  "no-reply@lu.ma",                    // Luma AI events
  "newsletter@wandb.ai",              // Weights & Biases events
  "newsletter@huggingface.co",        // Hugging Face community
  // ── AI Courses & Certifications ──
  "no-reply@deeplearning.ai",         // DeepLearning.AI (The Batch + course launches)
  "no-reply@coursera.org",            // Coursera AI/ML courses
  "edx@emails.edx.org",               // edX AI programs
  "cloudskills-noreply@google.com",   // Google Cloud Skills / AI training
  "no-reply@training.aws",            // AWS Training & Certification
  "microsoft-noreply@microsoft.com",  // Microsoft Learn AI skills
  "newsletter@towardsai.net",         // Towards AI (Learn AI Together)
  "no-reply@kaggle.com",              // Kaggle courses & competitions
  "nvidia@e.nvidia.com",              // NVIDIA Deep Learning Institute
];

// ─── Server-side processing (canonical prompt lives in lib/process-article.js) ──
async function processViaServer(input) {
  const res = await fetch("/api/process", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}

async function callClaudeWithMCP(messages, mcpServers) {
  const res = await fetch("/api/claude", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      model: "smart", // tier — resolved to a current model ID by server.js /api/claude (lib/models.js)
      max_tokens: 1000,
      mcp_servers: mcpServers,
      messages,
    }),
  });
  if (!res.ok) throw new Error(`API error: ${res.status}`);
  return res.json();
}


// ─── Sub-components ───────────────────────────────────────────────────────────
// Publish bar for the score badge colour. MENA rows clear a lower floor (3.4) than
// global news (3.8) — keep in sync with decide() in lib/process-article.js, or the
// dashboard will paint a published Gulf story red.
const publishBarFor = (art) => (art?.isMena ? 3.4 : 3.8);

const ScoreBar = ({ label, value }) => {
  const pct   = (value / 5) * 100;
  const color = value >= 4 ? "#00e5a0" : value >= 3 ? "#f0b429" : "#ff4757";
  return (
    <div style={{ marginBottom: 7 }}>
      <div style={{ display:"flex", justifyContent:"space-between", fontSize:11, color:"#8a9bb0", marginBottom:3 }}>
        <span>{label}</span>
        <span style={{ color, fontWeight:700 }}>{value}/5</span>
      </div>
      <div style={{ height:4, background:"#1e2d3d", borderRadius:2, overflow:"hidden" }}>
        <div style={{ width:`${pct}%`, height:"100%", background:color, borderRadius:2, transition:"width 0.8s ease" }} />
      </div>
    </div>
  );
};

const Badge = ({ decision }) => {
  const map = {
    editor:  { bg:"#1a3a2a", color:"#00e5a0", border:"#00e5a0", label:"🌟 اختيار المحرر" },
    publish: { bg:"#1a2d1a", color:"#7ed321", border:"#7ed321", label:"✅ يُنشر"          },
    reject:  { bg:"#2d1a1a", color:"#ff4757", border:"#ff4757", label:"❌ مرفوض"          },
  };
  const c = map[decision] || map.reject;
  return (
    <span style={{ display:"inline-block", padding:"3px 12px", borderRadius:20, fontSize:12,
      fontWeight:700, background:c.bg, color:c.color, border:`1px solid ${c.border}` }}>
      {c.label}
    </span>
  );
};

const Spinner = () => (
  <div style={{ width:14, height:14, border:"2px solid #1a2d3d", borderTop:"2px solid #00e5a0",
    borderRadius:"50%", animation:"spin 0.8s linear infinite", display:"inline-block" }} />
);

const SeoField = ({ label, value, color="#5a7a90" }) => value ? (
  <div style={{ marginBottom:6 }}>
    <span style={{ fontSize:10, color:"#3a5a70", marginLeft:6 }}>{label}</span>
    <span style={{ fontSize:12, color }}>{value}</span>
  </div>
) : null;

// ─── Login Screen ─────────────────────────────────────────────────────────────
function LoginScreen({ onLogin }) {
  const [password, setPassword] = useState("");
  const [error,    setError]    = useState("");
  const [shake,    setShake]    = useState(false);
  const [busy,     setBusy]     = useState(false);

  const T = {
    bg:"#080f17", surface:"#0d1b2a", surfaceAlt:"#06111a",
    border:"#132030", text:"#c8d8e8", textDim:"#5a7a90", textFaint:"#2a4a5a",
    green:"#00e5a0", red:"#ff4757",
  };

  const attempt = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await fetch("/api/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      if (res.ok) {
        onLogin();
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.error || "كلمة المرور غير صحيحة");
        setShake(true);
        setTimeout(() => setShake(false), 500);
        setTimeout(() => setError(""), 2500);
        setPassword("");
      }
    } catch {
      setError("تعذّر الاتصال بالخادم");
      setTimeout(() => setError(""), 2500);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ minHeight:"100vh", background:T.bg, display:"flex", alignItems:"center",
      justifyContent:"center", fontFamily:"'IBM Plex Sans Arabic', sans-serif", direction:"rtl" }}>
      <style>{`@keyframes shake{0%,100%{transform:translateX(0)}20%,60%{transform:translateX(-8px)}40%,80%{transform:translateX(8px)}}`}</style>
      <div style={{ width:360, animation: shake ? "shake 0.4s ease" : "none" }}>
        <div style={{ textAlign:"center", marginBottom:32 }}>
          <div style={{ fontSize:36, fontWeight:800, color:T.green, letterSpacing:-1 }}>⚡ موجز</div>
          <div style={{ fontSize:13, color:T.textDim, marginTop:6 }}>لوحة تحكم نشرة الذكاء الاصطناعي</div>
        </div>
        <div style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:14, padding:32 }}>
          <div style={{ fontSize:15, fontWeight:700, color:T.text, marginBottom:24, textAlign:"center" }}>
            تسجيل الدخول
          </div>
          <div style={{ marginBottom:16 }}>
            <label style={{ fontSize:12, color:T.textDim, display:"block", marginBottom:6 }}>كلمة المرور</label>
            <input
              type="password" value={password}
              onChange={e => setPassword(e.target.value)}
              onKeyDown={e => e.key === "Enter" && attempt()}
              placeholder="••••••••" autoFocus
              style={{ width:"100%", boxSizing:"border-box",
                background:T.surfaceAlt, border:`1px solid ${error ? T.red : T.border}`,
                borderRadius:8, padding:"10px 14px", color:T.text, fontSize:14,
                fontFamily:"inherit", outline:"none", transition:"border-color 0.2s" }}
            />
            {error && <div style={{ fontSize:12, color:T.red, marginTop:6 }}>{error}</div>}
          </div>
          <button onClick={attempt} disabled={busy}
            style={{ width:"100%", padding:"11px", borderRadius:8, border:"none",
              cursor: busy ? "not-allowed" : "pointer",
              background:T.green, color:"#080f17", fontSize:14, fontWeight:700,
              fontFamily:"inherit", opacity: busy ? 0.7 : 1 }}>
            {busy ? "..." : "دخول"}
          </button>
        </div>
        <div style={{ textAlign:"center", marginTop:16, fontSize:11, color:T.textFaint }}>
          Dashboard.mojaz.ai
        </div>
      </div>
    </div>
  );
}

// ─── Main App ─────────────────────────────────────────────────────────────────
export default function App() {
  const [loggedIn,      setLoggedIn]      = useState(null);
  const [tab,           setTab]           = useState("pipeline");
  const [stage,         setStage]         = useState("idle");
  const [daysBack,      setDaysBack]      = useState(1);
  const [emails,        setEmails]        = useState([]);
  const [articles,      setArticles]      = useState([]);
  const [articleCounts, setArticleCounts] = useState({ total:0, editor:0, publish:0, reject:0, pending:0 });
  const [hasMore,       setHasMore]       = useState(true);
  const [loadingMore,   setLoadingMore]   = useState(false);
  const [queueArticles, setQueueArticles] = useState([]);
  const [processingIdx, setProcessingIdx] = useState(-1);
  const [wpStatus,      setWpStatus]      = useState({});
  const [expandedId,    setExpandedId]    = useState(null);
  const [seoExpandedId, setSeoExpandedId] = useState(null);
  const [manualContent,   setManualContent]   = useState("");
  const [manualSourceUrl, setManualSourceUrl] = useState("");
  const [manualBusy,      setManualBusy]      = useState(false);
  const [log,           setLog]           = useState([]);
  const [editTitle,     setEditTitle]     = useState({});
  const [wpCategories,  setWpCategories]  = useState([]);
  const [manualImage,   setManualImage]   = useState(null);   // File object
  const [manualImgPrev, setManualImgPrev] = useState(null);   // data URL for preview
  const [pendingImages, setPendingImages] = useState({});     // { [articleId]: { base64, filename, mimeType, previewUrl } }

  const addLog = useCallback((msg, type = "info") =>
    setLog(prev => [...prev.slice(-99), { msg, type, time: new Date().toLocaleTimeString("ar") }])
  , []);

  const loadCategories = () => {
    fetch("/api/wp-categories")
      .then(r => r.json())
      .then(data => { if (Array.isArray(data)) setWpCategories(data); })
      .catch(() => {});
  };

  // ── Session + load saved articles ──
  useEffect(() => {
    fetch("/api/auth")
      .then(r => {
        if (r.ok) { setLoggedIn(true); loadArticles(); loadCategories(); loadCounts(); loadQueue(); }
        else setLoggedIn(false);
      })
      .catch(() => setLoggedIn(false));
  }, []);

  const PAGE_SIZE = 50;

  const loadArticles = () => {
    fetch(`/api/articles?limit=${PAGE_SIZE}&offset=0`)
      .then(r => r.json())
      .then(data => {
        if (Array.isArray(data)) {
          setArticles(data);
          setHasMore(data.length >= PAGE_SIZE);
          const statusMap = {};
          data.forEach(a => { if (a.wpStatus === "published") statusMap[a.id] = "published"; });
          setWpStatus(statusMap);
        }
      })
      .catch(() => {});
  };

  const loadMoreArticles = () => {
    setLoadingMore(true);
    fetch(`/api/articles?limit=${PAGE_SIZE}&offset=${articles.length}`)
      .then(r => r.json())
      .then(data => {
        if (Array.isArray(data)) {
          setArticles(prev => [...prev, ...data]);
          setHasMore(data.length >= PAGE_SIZE);
          const statusMap = {};
          data.forEach(a => { if (a.wpStatus === "published") statusMap[a.id] = "published"; });
          setWpStatus(prev => ({ ...prev, ...statusMap }));
        }
        setLoadingMore(false);
      })
      .catch(() => setLoadingMore(false));
  };

  const loadCounts = () => {
    fetch("/api/articles/counts")
      .then(r => r.json())
      .then(data => { if (data && typeof data.total === "number") setArticleCounts(data); })
      .catch(() => {});
  };

  const loadQueue = () => {
    fetch("/api/articles?status=pending&limit=200")
      .then(r => r.json())
      .then(data => { if (Array.isArray(data)) setQueueArticles(data); })
      .catch(() => {});
  };

  const logout = async () => {
    await fetch("/api/logout", { method: "POST" }).catch(() => {});
    setLoggedIn(false);
  };

  // ── Save article to DB ──
  const saveArticle = async (art) => {
    await fetch("/api/articles", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(art),
    }).catch(() => {});
  };

  const uploadImage = async (file) => {
    const dataBase64 = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload  = () => resolve(reader.result.split(",")[1]);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
    const res = await fetch("/api/upload-media", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ filename: file.name, mimeType: file.type, dataBase64 }),
    });
    if (!res.ok) { const d = await res.json(); throw new Error(d.error || "فشل رفع الصورة"); }
    return res.json(); // { mediaId, mediaUrl }
  };

  const upsert = (art) => {
    setArticles(prev => {
      const i = prev.findIndex(a => a.id === art.id);
      if (i >= 0) { const n = [...prev]; n[i] = art; return n; }
      return [art, ...prev];
    });
    saveArticle(art);
    loadCounts();
  };

  // ── Fetch emails ──
  const fetchEmails = async () => {
    setStage("fetching"); setEmails([]); setLog([]);
    addLog("جارٍ الاتصال بـ Gmail...", "info");
    try {
      const q = `(${NEWSLETTER_SENDERS.map(s=>`from:${s}`).join(" OR ")}) newer_than:${daysBack}d`;
      const data = await callClaudeWithMCP(
        [{ role:"user", content:`Search Gmail for AI newsletters: "${q}". Return ONLY a valid JSON array (no markdown) of up to 25 emails, each with fields: id, subject, from, snippet, date.` }],
        [{ type:"url", url:"https://gmail.mcp.claude.com/mcp", name:"gmail" }]
      );
      let list = [];
      for (const b of data.content || []) {
        const txt = b.type === "text" ? b.text : b.content?.[0]?.text || "";
        if (!txt) continue;
        try {
          const cleaned = txt.replace(/```json|```/g,"").trim();
          const parsed  = JSON.parse(cleaned);
          if (Array.isArray(parsed) && parsed.length) { list = parsed; break; }
        } catch {}
        const m = txt.match(/\[[\s\S]*?\]/);
        if (m) { try { list = JSON.parse(m[0]); break; } catch {} }
      }
      setEmails(list);
      addLog(`✓ تم جلب ${list.length} رسالة`, "success");
    } catch (e) {
      addLog(`خطأ: ${e.message}`, "error");
    }
    setStage("idle");
  };

  // ── Get full email body (HTML preferred for media/link extraction) ──
  const getBody = async (id) => {
    try {
      const data = await callClaudeWithMCP(
        [{ role:"user", content:`Read Gmail message ID: ${id}. Return the full raw HTML body of the email exactly as-is, preserving all <img>, <a>, and other HTML tags. Do not summarise, do not convert to plain text, do not add any explanation. Return only the HTML.` }],
        [{ type:"url", url:"https://gmail.mcp.claude.com/mcp", name:"gmail" }]
      );
      return (data.content||[]).filter(b=>b.type==="text").map(b=>b.text).join("\n").substring(0, 50000);
    } catch { return ""; }
  };

  // ── Process one email ──
  const processOne = async (email, idx) => {
    setProcessingIdx(idx);
    addLog(`⏳ معالجة: ${email.subject}`, "info");
    try {
      let body = email.body || email.snippet || "";
      if (body.length < 200) {
        addLog("جارٍ جلب النص الكامل...", "info");
        body = await getBody(email.id);
      }
      if (body.length < 50) { addLog(`⚠️ محتوى فارغ: ${email.subject}`, "warn"); return null; }
      const isHtml = body.trimStart().startsWith("<");
      const scored = await processViaServer({
        title: email.subject || "", sourceUrl: "",
        sourceText: isHtml ? "" : body, rawHtml: isHtml ? body : "",
        emailSubject: email.subject, emailFrom: email.from, emailDate: email.date,
      });
      addLog(`✓ ${scored.title || email.subject} — ${scored.total}/5`, scored.decision==="reject"?"warn":"success");
      return { ...scored, emailSubject:email.subject, emailFrom:email.from, emailDate:email.date, id:email.id, createdAt:new Date().toISOString() };
    } catch (e) {
      addLog(`خطأ في المعالجة: ${e.message}`, "error");
      return null;
    }
  };

  const processAll = async () => {
    setStage("processing"); setArticles([]);
    for (let i=0; i<emails.length; i++) {
      const art = await processOne(emails[i], i);
      if (art) upsert(art);
    }
    setProcessingIdx(-1); setStage("done");
    addLog("✅ اكتملت المعالجة", "success");
  };

  const processSingle = async (email, idx) => {
    setStage("processing");
    const art = await processOne(email, idx);
    if (art) upsert(art);
    setProcessingIdx(-1); setStage("idle");
  };

  const processManual = async () => {
    if (!manualContent.trim()) return;
    setManualBusy(true);
    addLog("معالجة المحتوى اليدوي...", "info");
    try {
      const isHtml = manualContent.trimStart().startsWith("<");
      const scored = await processViaServer({
        title: "", sourceUrl: manualSourceUrl || "",
        sourceText: isHtml ? "" : manualContent, rawHtml: isHtml ? manualContent : "",
        emailSubject: "إدخال يدوي", emailFrom: "manual", emailDate: new Date().toISOString(),
      });
      const wpFeaturedMediaId  = null;
      const wpFeaturedMediaUrl = null;
      const artId = `manual_${Date.now()}`;
      const art = { ...scored, emailSubject:"إدخال يدوي", emailFrom:"manual",
        emailDate: new Date().toLocaleDateString("ar"), id: artId,
        wpFeaturedMediaId, wpFeaturedMediaUrl, createdAt:new Date().toISOString(),
        sourceUrl: manualSourceUrl || null };
      upsert(art);
      // Store image for upload at publish time (not now)
      if (manualImage) {
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload  = () => resolve(reader.result.split(",")[1]);
          reader.onerror = reject;
          reader.readAsDataURL(manualImage);
        });
        setPendingImages(p => ({ ...p, [artId]: { base64, filename: manualImage.name, mimeType: manualImage.type, previewUrl: manualImgPrev } }));
      }
      addLog(`✓ تمت المعالجة — ${scored.total}/5`, scored.decision==="reject"?"warn":"success");
      setManualContent("");
      setManualSourceUrl("");
      setManualImage(null);
      setManualImgPrev(null);
      setTab("articles");
    } catch (e) { addLog(`خطأ: ${e.message}`, "error"); }
    setManualBusy(false);
  };

  // ── Publish to WordPress ──
  const publish = async (art) => {
    const id = art.id;
    setWpStatus(p=>({...p,[id]:"publishing"}));
    try {
      // Upload pending image now (only at publish time)
      let wpFeaturedMediaId = art.wpFeaturedMediaId || null;
      if (pendingImages[id]) {
        addLog("جارٍ رفع الصورة...", "info");
        try {
          const { base64, filename, mimeType } = pendingImages[id];
          const res2 = await fetch("/api/upload-media", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ filename, mimeType, dataBase64: base64 }),
          });
          if (!res2.ok) { const d = await res2.json(); throw new Error(d.error || "فشل رفع الصورة"); }
          const uploaded = await res2.json();
          wpFeaturedMediaId = uploaded.mediaId;
          const updated = { ...art, wpFeaturedMediaId: uploaded.mediaId, wpFeaturedMediaUrl: uploaded.mediaUrl };
          saveArticle(updated); // best-effort DB save
          setArticles(prev => prev.map(a => a.id === id ? updated : a));
          setPendingImages(p => { const n = {...p}; delete n[id]; return n; });
          addLog("✓ تم رفع الصورة", "success");
        } catch (e) { addLog(`⚠️ فشل رفع الصورة: ${e.message}`, "warn"); }
      }

      const finalTitle = editTitle[id] || art.title;
      const res = await fetch(`/api/publish/${id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title: finalTitle, wpFeaturedMediaId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "فشل النشر");
      setWpStatus(p=>({...p,[id]:"published"}));
      setArticles(prev => prev.map(a => a.id===id ? {...a, wpPostId:data.wpPostId, wpUrl:data.wpUrl, wpStatus:"published"} : a));
      addLog(`🚀 نُشر: ${finalTitle}`, "success");
    } catch (e) {
      setWpStatus(p=>({...p,[id]:"error"}));
      addLog(`خطأ في النشر: ${e.message}`, "error");
    }
  };

  const publishAll = () =>
    articles.filter(a => a.decision !== "reject" && wpStatus[a.id] !== "published").forEach(publish);

  // ── Computed ──
  const publishable = articles.filter(a => a.decision !== "reject");
  const stats = {
    total:   articleCounts.total,
    editor:  articleCounts.editor,
    publish: articleCounts.publish,
    reject:  articleCounts.reject,
  };

  // ─── Style tokens ─────────────────────────────────────────────────────────────
  const T = {
    bg:        "#080f17",
    surface:   "#0d1b2a",
    surfaceAlt:"#06111a",
    border:    "#132030",
    borderHover:"#1e3548",
    text:      "#c8d8e8",
    textDim:   "#5a7a90",
    textFaint: "#2a4a5a",
    green:     "#00e5a0",
    yellow:    "#f0b429",
    red:       "#ff4757",
    lime:      "#7ed321",
  };

  const css = {
    app:      { minHeight:"100vh", background:T.bg, color:T.text, fontFamily:"'IBM Plex Sans Arabic', sans-serif", direction:"rtl" },
    header:   { background:`linear-gradient(135deg,#0d1b2a,#0a1628)`, borderBottom:`1px solid ${T.border}`,
                padding:"14px 28px", display:"flex", alignItems:"center", justifyContent:"space-between",
                position:"sticky", top:0, zIndex:100 },
    sidebar:  { width:230, background:"#0a1420", borderLeft:`1px solid ${T.border}`,
                minHeight:"calc(100vh - 55px)", padding:"16px 0", flexShrink:0 },
    main:     { flex:1, padding:24, overflowY:"auto", maxHeight:"calc(100vh - 55px)" },
    card:     { background:T.surface, border:`1px solid ${T.border}`, borderRadius:10, padding:20, marginBottom:16 },
    navItem:  (active) => ({
      padding:"10px 18px", cursor:"pointer", fontSize:13, display:"flex", alignItems:"center", gap:8,
      color: active ? T.green : T.textDim,
      background: active ? "#0d1e2e" : "transparent",
      borderRight: active ? `3px solid ${T.green}` : "3px solid transparent",
      transition:"all 0.15s",
    }),
    btn: (v="primary") => ({
      padding:"8px 16px", borderRadius:7, border:"none", cursor:"pointer", fontSize:13,
      fontWeight:600, fontFamily:"inherit", transition:"all 0.15s",
      ...(v==="primary" ? { background:T.green,  color:"#080f17" } :
          v==="editor"  ? { background:"#00e5a0", color:"#080f17", boxShadow:"0 0 12px #00e5a044" } :
          v==="danger"  ? { background:T.red,    color:"#fff"    } :
          v==="ghost"   ? { background:"transparent", color:T.textDim, border:`1px solid ${T.border}` } :
                          { background:"#132030", color:T.text }),
    }),
    input:    { background:T.surfaceAlt, border:`1px solid ${T.border}`, borderRadius:7,
                padding:"8px 12px", color:T.text, fontSize:13, fontFamily:"inherit" },
    tag:      (color="#4a8fa0") => ({
      display:"inline-block", padding:"2px 8px", borderRadius:12, fontSize:10,
      background:color+"22", color, border:`1px solid ${color}44`,
    }),
    emailRow: (done, busy) => ({
      padding:"12px 14px", borderRadius:8, marginBottom:8, cursor:"pointer", transition:"all 0.2s",
      border:`1px solid ${done ? T.green : T.border}`,
      background: done ? "#0d2218" : busy ? "#0d1a28" : "#08131d",
    }),
    articleCard: (d) => ({
      background:T.surface, borderRadius:10, padding:20, marginBottom:14,
      border:`1px solid ${d==="editor" ? T.green : d==="publish" ? T.lime : "#2a1a1a"}`,
    }),
    logLine:  (t) => ({
      fontSize:11, padding:"2px 0", fontFamily:"monospace",
      color: t==="error"?"#ff4757":t==="success"?"#00e5a0":t==="warn"?"#f0b429":"#4a6580",
    }),
    textarea: { background:T.surfaceAlt, border:`1px solid ${T.border}`, borderRadius:7,
                padding:"12px 14px", color:T.text, fontSize:13, fontFamily:"inherit",
                minHeight:200, resize:"vertical", width:"100%" },
  };

  if (loggedIn === null) return (
    <div style={{ minHeight:"100vh", background:"#080f17", display:"flex", alignItems:"center",
      justifyContent:"center", fontFamily:"'IBM Plex Sans Arabic', sans-serif", color:"#00e5a0", fontSize:14 }}>
      جارٍ التحقق...
    </div>
  );
  if (!loggedIn) return <LoginScreen onLogin={() => { setLoggedIn(true); loadArticles(); loadCategories(); loadCounts(); loadQueue(); }} />;

  return (
    <div style={css.app}>
      {/* ── Header ── */}
      <div style={css.header}>
        <div>
          <div style={{ fontSize:22, fontWeight:800, color:T.green, letterSpacing:-0.5 }}>⚡ موجز</div>
          <div style={{ fontSize:11, color:T.textFaint, marginTop:2 }}>لوحة تحكم نشرة الذكاء الاصطناعي</div>
        </div>
        <div style={{ display:"flex", gap:8, alignItems:"center" }}>
          {stage==="processing" && (
            <div style={{ fontSize:12, color:T.yellow, display:"flex", alignItems:"center", gap:6 }}>
              <Spinner /> جارٍ المعالجة...
            </div>
          )}
          {stage==="fetching" && (
            <div style={{ fontSize:12, color:T.green, display:"flex", alignItems:"center", gap:6 }}>
              <Spinner /> جارٍ الجلب...
            </div>
          )}
          <div style={{ fontSize:11, color:T.textFaint, background:T.surfaceAlt,
            padding:"4px 12px", borderRadius:20, border:`1px solid ${T.border}` }}>mojaz.ai</div>
          <button onClick={logout} style={{ background:"transparent", border:`1px solid ${T.border}`,
            borderRadius:20, padding:"4px 12px", color:T.textDim, fontSize:11, cursor:"pointer",
            fontFamily:"inherit" }}>خروج</button>
        </div>
      </div>

      <div style={{ display:"flex" }}>
        {/* ── Sidebar ── */}
        <div style={css.sidebar}>
          {[
            { id:"pipeline", icon:"⚡", label:"خط المعالجة"             },
            { id:"queue",    icon:"📤", label:`الطابور (${articleCounts.pending})` },
            { id:"articles", icon:"📰", label:`المقالات (${articleCounts.total})` },
            { id:"manual",   icon:"✏️",  label:"إدخال يدوي"             },
            { id:"log",      icon:"📋", label:"السجل"                   },
          ].map(t => (
            <div key={t.id} style={css.navItem(tab===t.id)} onClick={()=>setTab(t.id)}>
              <span>{t.icon}</span><span>{t.label}</span>
            </div>
          ))}

          <div style={{ padding:"20px 16px 0", borderTop:`1px solid ${T.border}`, marginTop:20 }}>
            <div style={{ fontSize:10, color:T.textFaint, marginBottom:10, letterSpacing:1 }}>إحصائيات</div>
            {[
              { label:"🌟 اختيار المحرر", val:stats.editor,  color:T.green },
              { label:"✅ تُنشر",          val:stats.publish, color:T.lime  },
              { label:"❌ مرفوضة",         val:stats.reject,  color:T.red   },
            ].map(r => (
              <div key={r.label} style={{ display:"flex", justifyContent:"space-between", fontSize:12, marginBottom:6, color:T.textDim }}>
                <span>{r.label}</span>
                <span style={{ color:r.color, fontWeight:700 }}>{r.val}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ── Main ── */}
        <div style={css.main}>

          {/* ════ PIPELINE TAB ════ */}
          {tab==="pipeline" && (
            <div>
              <h2 style={{ fontSize:17, fontWeight:700, color:"#e0eaf5", marginBottom:20 }}>جلب النشرات البريدية</h2>
              <div style={{ ...css.card, display:"flex", gap:12, alignItems:"center", flexWrap:"wrap" }}>
                <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                  <span style={{ fontSize:12, color:T.textDim }}>آخر</span>
                  <select value={daysBack} onChange={e=>setDaysBack(+e.target.value)}
                    style={{ ...css.input, width:80 }}>
                    {[1,2,3,7].map(d=><option key={d} value={d}>{d} {d===1?"يوم":"أيام"}</option>)}
                  </select>
                </div>
                <button style={css.btn("primary")} onClick={fetchEmails}
                  disabled={stage!=="idle" && stage!=="done"}>
                  🔄 جلب النشرات
                </button>
                {emails.length>0 && (
                  <button style={css.btn()} onClick={processAll} disabled={stage==="processing"}>
                    {stage==="processing"
                      ? `⏳ معالجة ${processingIdx+1}/${emails.length}...`
                      : `⚡ معالجة الكل (${emails.length})`}
                  </button>
                )}
              </div>

              <div style={{ ...css.card }}>
                <div style={{ fontSize:12, color:T.textDim, marginBottom:10 }}>
                  المصادر المفعّلة ({NEWSLETTER_SENDERS.length})
                </div>
                <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                  {NEWSLETTER_SENDERS.map(s=>(
                    <span key={s} style={css.tag("#4a8fa0")}>{s.split("@")[1]}</span>
                  ))}
                </div>
              </div>

              {emails.length>0 && (
                <div>
                  <div style={{ fontSize:12, color:T.textDim, marginBottom:10 }}>
                    {emails.length} رسالة — انقر "معالجة" لتحويل مقالة واحدة
                  </div>
                  {emails.map((email, idx) => {
                    const done = articles.find(a=>a.id===email.id);
                    const busy = processingIdx===idx;
                    return (
                      <div key={email.id||idx} style={css.emailRow(!!done, busy)}>
                        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
                          <div style={{ flex:1 }}>
                            <div style={{ fontSize:13, fontWeight:600, color:done?T.green:"#e0eaf5", marginBottom:3 }}>
                              {busy?"⏳ ":done?"✓ ":""}{email.subject}
                            </div>
                            <div style={{ fontSize:11, color:T.textDim }}>{email.from} · {email.date}</div>
                            {email.snippet && (
                              <div style={{ fontSize:11, color:"#3a5a70", marginTop:4, lineHeight:1.5 }}>
                                {email.snippet.substring(0,130)}…
                              </div>
                            )}
                          </div>
                          <div style={{ display:"flex", gap:6, alignItems:"center", marginRight:10, flexShrink:0 }}>
                            {done && <Badge decision={done.decision} />}
                            {!done && !busy && (
                              <button style={{ ...css.btn("ghost"), fontSize:11, padding:"4px 10px" }}
                                onClick={()=>processSingle(email,idx)}>
                                معالجة
                              </button>
                            )}
                            {busy && <Spinner />}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {emails.length===0 && stage==="idle" && (
                <div style={{ textAlign:"center", padding:60, color:T.textFaint }}>
                  <div style={{ fontSize:42, marginBottom:12 }}>📭</div>
                  <div style={{ fontSize:14 }}>اضغط "جلب النشرات" للبدء</div>
                </div>
              )}
            </div>
          )}

          {/* ════ QUEUE TAB ════ */}
          {tab==="queue" && (() => {
            const sorted = [...queueArticles].sort((a, b) => {
              const aEditor = a.decision === "editor" ? 0 : 1;
              const bEditor = b.decision === "editor" ? 0 : 1;
              if (aEditor !== bEditor) return aEditor - bEditor;
              return new Date(a.createdAt) - new Date(b.createdAt);
            });
            const rejectFromQueue = async (art) => {
              const updated = { ...art, wpStatus: "rejected" };
              await fetch("/api/articles", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(updated),
              });
              setQueueArticles(prev => prev.filter(a => a.id !== art.id));
              setArticleCounts(prev => ({ ...prev, pending: Math.max(0, prev.pending - 1) }));
            };
            const timeAgo = (dateStr) => {
              if (!dateStr) return "";
              const mins = Math.floor((Date.now() - new Date(dateStr).getTime()) / 60000);
              if (mins < 60) return `منذ ${mins} دقيقة`;
              const hrs = Math.floor(mins / 60);
              if (hrs < 24) return `منذ ${hrs} ساعة`;
              return `منذ ${Math.floor(hrs / 24)} يوم`;
            };
            return (
            <div>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
                <h2 style={{ fontSize:17, fontWeight:700, color:"#e0eaf5" }}>طابور النشر ({sorted.length})</h2>
              </div>

              {sorted.length === 0 && (
                <div style={{ textAlign:"center", padding:60, color:T.textFaint }}>
                  <div style={{ fontSize:42, marginBottom:12 }}>✅</div>
                  <div>الطابور فارغ — لا توجد مقالات بانتظار النشر</div>
                </div>
              )}

              {sorted.map((art, idx) => (
                <div key={art.id} style={{
                  ...css.articleCard(art.decision),
                  display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:16
                }}>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6, flexWrap:"wrap" }}>
                      <span style={{ fontSize:11, color:T.textFaint, fontFamily:"monospace" }}>#{idx+1}</span>
                      {art.decision === "editor" && (
                        <span style={{ fontSize:10, padding:"2px 8px", borderRadius:12, background:"#0d2a1e", color:T.green, border:`1px solid ${T.green}44` }}>🌟 اختيار المحرر</span>
                      )}
                      {art.wpCategoryId && (() => {
                        const cat = wpCategories.find(c => c.id === art.wpCategoryId);
                        return cat ? (
                          <span style={{ fontSize:10, padding:"2px 8px", borderRadius:12, background:"#1a2d4a", color:"#5a9fd4", border:"1px solid #2a4a6a" }}>
                            {cat.name}
                          </span>
                        ) : null;
                      })()}
                      {art.sourceType && (
                        <span style={{ fontSize:10, padding:"2px 8px", borderRadius:12, background:"#1a1a2e", color:"#8a8adf", border:"1px solid #2a2a4a" }}>
                          {art.sourceType}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize:14, fontWeight:700, color:"#e0eaf5", marginBottom:6, lineHeight:1.6 }}>{art.title}</div>
                    <div style={{ fontSize:12, color:T.textDim, marginBottom:4 }}>{art.subtitle}</div>
                    <div style={{ display:"flex", gap:12, fontSize:11, color:T.textFaint, flexWrap:"wrap" }}>
                      {art.sourceName && <span>📰 {art.sourceName}</span>}
                      {art.reporter && <span>✍️ {art.reporter.split("|")[0].trim()}</span>}
                      <span>⏱️ {timeAgo(art.createdAt)}</span>
                    </div>
                  </div>
                  <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:8, flexShrink:0 }}>
                    <div style={{
                      width:44, height:44, borderRadius:10, display:"flex", alignItems:"center", justifyContent:"center",
                      fontSize:16, fontWeight:800, fontFamily:"monospace",
                      background: art.total >= 4.5 ? "#0d2a1e" : art.total >= publishBarFor(art) ? "#1a2a0d" : "#2a1a1a",
                      color: art.total >= 4.5 ? T.green : art.total >= publishBarFor(art) ? T.lime : T.red,
                      border: `1px solid ${art.total >= 4.5 ? T.green : art.total >= publishBarFor(art) ? T.lime : T.red}44`,
                    }}>{art.total?.toFixed(1)}</div>
                    <button
                      onClick={() => { if (window.confirm(`رفض: ${art.title.substring(0,40)}...؟`)) rejectFromQueue(art); }}
                      style={{ ...css.btn("danger"), fontSize:11, padding:"4px 10px" }}
                    >رفض</button>
                  </div>
                </div>
              ))}
            </div>
            );
          })()}

          {/* ════ ARTICLES TAB ════ */}
          {tab==="articles" && (
            <div>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
                <h2 style={{ fontSize:17, fontWeight:700, color:"#e0eaf5" }}>المقالات المعالجة</h2>
                {publishable.filter(a=>wpStatus[a.id]!=="published").length>0 && (
                  <button style={css.btn("primary")} onClick={publishAll}>
                    🚀 نشر الكل ({publishable.filter(a=>wpStatus[a.id]!=="published").length})
                  </button>
                )}
              </div>

              {articles.length===0 && (
                <div style={{ textAlign:"center", padding:60, color:T.textFaint }}>
                  <div style={{ fontSize:42, marginBottom:12 }}>📄</div>
                  <div>لا توجد مقالات — ابدأ من تبويب خط المعالجة أو الإدخال اليدوي</div>
                </div>
              )}

              {[...articles].sort((a,b)=>{
                const aPublished = wpStatus[a.id]==="published" ? 1 : 0;
                const bPublished = wpStatus[b.id]==="published" ? 1 : 0;
                if (aPublished !== bPublished) return aPublished - bPublished;
                return new Date(b.createdAt)-new Date(a.createdAt);
              }).map(art => (
                <div key={art.id} style={css.articleCard(art.decision)}>

                  {/* ── Title & score row ── */}
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:12 }}>
                    <div style={{ flex:1, marginLeft:16 }}>
                      <input
                        value={editTitle[art.id] ?? art.title}
                        onChange={e=>setEditTitle(p=>({...p,[art.id]:e.target.value}))}
                        style={{ ...css.input, width:"100%", fontSize:14, fontWeight:700,
                          color:"#e0eaf5", marginBottom:6, background:"transparent",
                          border:`1px solid ${editTitle[art.id]!=null ? T.green : "transparent"}`,
                          borderRadius:6, padding:"4px 8px" }}
                        dir="rtl"
                      />
                      <div style={{ display:"flex", gap:6, alignItems:"center", flexWrap:"wrap", marginBottom:4 }}>
                        {art.wpCategoryId && (() => {
                          const cat = wpCategories.find(c => c.id === art.wpCategoryId);
                          return cat ? (
                            <span style={{ display:"inline-block", padding:"2px 8px", borderRadius:12, fontSize:10,
                              background:"#1a2d4a", color:"#5a9fd4", border:"1px solid #2a4a6a" }}>
                              🏷️ {cat.name}
                            </span>
                          ) : null;
                        })()}
                      </div>
                      <div style={{ fontSize:12, color:T.textDim, marginBottom:4 }}>{art.subtitle}</div>
                      <div style={{ fontSize:11, color:T.textFaint }}>{art.emailFrom} · {art.emailDate}</div>
                      {art.publishedAt && (
                        <div style={{ fontSize:11, color:T.green, marginTop:2 }}>
                          📅 نُشر: {new Date(art.publishedAt+"Z").toLocaleString("ar-EG", { year:"numeric", month:"short", day:"numeric", hour:"2-digit", minute:"2-digit" })}
                        </div>
                      )}
                      {art.gscImpressions > 0 && (
                        <div style={{ display:"flex", gap:10, marginTop:3, fontSize:11, color:"#8ab4f8" }}>
                          <span>👆 {art.gscClicks} نقرة</span>
                          <span>👁️ {art.gscImpressions} ظهور</span>
                          <span>📊 {(art.gscCtr*100).toFixed(1)}%</span>
                          <span>📍 {art.gscPosition.toFixed(1)}</span>
                        </div>
                      )}
                      {art.wpUrl && (
                        <a href={art.wpUrl} target="_blank" rel="noreferrer"
                          style={{ fontSize:11, color:T.green, display:"inline-block", marginTop:4 }}>
                          🔗 عرض المقال المنشور
                        </a>
                      )}
                    </div>
                    {(art.wpFeaturedMediaUrl || pendingImages[art.id]) && (
                      <div style={{ flexShrink:0, marginRight:12, textAlign:"center" }}>
                        <img src={art.wpFeaturedMediaUrl || pendingImages[art.id]?.previewUrl} alt=""
                          style={{ width:80, height:60, objectFit:"cover", borderRadius:6,
                            border:`1px solid ${art.wpFeaturedMediaUrl ? T.border : T.yellow}` }} />
                        {pendingImages[art.id] && !art.wpFeaturedMediaUrl && (
                          <div style={{ fontSize:9, color:T.yellow, marginTop:2 }}>ترفع عند النشر</div>
                        )}
                      </div>
                    )}
                    <div style={{ textAlign:"center", flexShrink:0 }}>
                      <div style={{ fontSize:26, fontWeight:800, lineHeight:1,
                        color:art.total>=4.5?T.green:art.total>=publishBarFor(art)?T.lime:T.red, marginBottom:6 }}>
                        {art.total}
                      </div>
                      <Badge decision={art.decision} />
                    </div>
                  </div>

                  {/* ── Score bars ── */}
                  <div style={{ background:T.surfaceAlt, borderRadius:8, padding:14, marginBottom:12 }}>
                    <ScoreBar label="التأثير"         value={art.impact}       />
                    <ScoreBar label="المصداقية"       value={art.credibility}  />
                    <ScoreBar label="الجدة"           value={art.novelty}      />
                    <ScoreBar label="الفائدة العملية" value={art.practicality} />
                    <ScoreBar label="الراهنية"        value={art.recency}      />
                  </div>

                  {/* ── Alt titles ── */}
                  {art.altTitles?.length>0 && (
                    <div style={{ marginBottom:12 }}>
                      <div style={{ fontSize:11, color:T.textFaint, marginBottom:6 }}>عناوين بديلة:</div>
                      <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                        {art.altTitles.map((t,i)=>(
                          <span key={i} style={{ ...css.tag("#6a7fa0"), cursor:"pointer" }}
                            onClick={()=>setEditTitle(p=>({...p,[art.id]:t}))}>
                            {t}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* ── SEO Panel ── */}
                  <div style={{ marginBottom:12 }}>
                    <button style={{ ...css.btn("ghost"), fontSize:11, padding:"4px 12px", marginBottom:8 }}
                      onClick={()=>setSeoExpandedId(seoExpandedId===art.id?null:art.id)}>
                      {seoExpandedId===art.id ? "▲ إخفاء بيانات SEO" : "▼ بيانات AIOSEO"}
                    </button>
                    {seoExpandedId===art.id && (
                      <div style={{ background:T.surfaceAlt, borderRadius:8, padding:14,
                        border:`1px solid ${T.border}` }}>
                        <div style={{ fontSize:11, color:T.textFaint, marginBottom:10, letterSpacing:1 }}>
                          بيانات AIOSEO — سيتم إرسالها تلقائياً عند النشر
                        </div>
                        <SeoField label="عنوان SEO:"          value={art.metaTitle}  color={T.text} />
                        <SeoField label="الوصف:"              value={art.metaDesc}   color={T.textDim} />
                        <SeoField label="الكلمة المفتاحية:"   value={art.keyphrase}  color={T.green} />
                        <SeoField label="الكلمات المفتاحية:"  value={art.keywords}   color={T.textDim} />
                        <SeoField label="عنوان OG:"           value={art.ogTitle}    color={T.text} />
                        <SeoField label="وصف OG:"             value={art.ogDesc}     color={T.textDim} />
                        <SeoField label="الـ Slug:"           value={art.slug}       color={T.yellow} />
                        {art.wpTagIds?.length > 0 && (() => {
                          const TAG_MAP = {10:"OpenAI",11:"Google AI",12:"Gemini",13:"Claude",14:"Nvidia AI",15:"AI Agents",16:"RAG",17:"Generative AI",18:"LLM",19:"AI tools",32:"المنطقة",33:"lead"};
                          return <SeoField label="الوسوم:" value={art.wpTagIds.map(id=>TAG_MAP[id]||id).join(", ")} color="#a78bfa" />;
                        })()}
                      </div>
                    )}
                  </div>

                  {/* ── Actions ── */}
                  <div style={{ display:"flex", gap:8, flexWrap:"wrap", alignItems:"center" }}>
                    {art.decision === "reject" ? (
                      <span style={{ fontSize:12, color:T.red, padding:"6px 12px",
                        background:"#2d1a1a", borderRadius:7, border:"1px solid #ff475733" }}>
                        ❌ مرفوض — لا يُنشر (أقل من 3.8)
                      </span>
                    ) : (
                      <button
                        style={css.btn(wpStatus[art.id]==="published" ? "ghost" : art.decision==="editor" ? "editor" : "primary")}
                        onClick={()=>publish(art)}
                        disabled={wpStatus[art.id]==="publishing" || wpStatus[art.id]==="published"}>
                        {wpStatus[art.id]==="published"  ? "✓ نُشر"
                          : wpStatus[art.id]==="publishing" ? "جارٍ النشر..."
                          : wpStatus[art.id]==="error"    ? "⚠️ أعد المحاولة"
                          : art.decision==="editor"       ? "🌟 نشر — اختيار المحرر"
                          : "🚀 نشر الآن"}
                      </button>
                    )}
                    <button style={css.btn("ghost")}
                      onClick={()=>setExpandedId(expandedId===art.id?null:art.id)}>
                      {expandedId===art.id ? "إخفاء المحتوى" : "عرض المحتوى"}
                    </button>
                    <button style={css.btn("ghost")}
                      onClick={()=>{ navigator.clipboard.writeText(art.htmlContent); addLog("تم نسخ HTML","success"); }}>
                      📋 نسخ HTML
                    </button>
                  </div>

                  {/* ── Expanded HTML content ── */}
                  {expandedId===art.id && (
                    <div style={{ marginTop:14, background:T.surfaceAlt, borderRadius:8, padding:16,
                      border:`1px solid ${T.border}` }}>
                      <div style={{ fontSize:11, color:T.textFaint, marginBottom:8 }}>محتوى WordPress HTML:</div>
                      <div dangerouslySetInnerHTML={{ __html:art.htmlContent }}
                        style={{ fontSize:13, lineHeight:1.9, color:T.text, direction:"rtl" }} />
                    </div>
                  )}

                </div>
              ))}

              {hasMore && (
                <div style={{ textAlign:"center", padding:20 }}>
                  <button style={css.btn("primary")} onClick={loadMoreArticles} disabled={loadingMore}>
                    {loadingMore ? "جارٍ التحميل..." : `تحميل المزيد (${articles.length} من ${articleCounts.total})`}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ════ MANUAL TAB ════ */}
          {tab==="manual" && (
            <div>
              <h2 style={{ fontSize:17, fontWeight:700, color:"#e0eaf5", marginBottom:20 }}>إدخال يدوي</h2>
              <div style={css.card}>
                <div style={{ fontSize:13, color:T.textDim, marginBottom:12 }}>
                  الصق أي نص إنجليزي لتحويله إلى مقالة عربية مُقيَّمة مع بيانات AIOSEO
                </div>
                <textarea style={css.textarea} dir="ltr"
                  placeholder="Paste English article or newsletter content here..."
                  value={manualContent}
                  onChange={e=>setManualContent(e.target.value)} />

                {/* ── Source URL ── */}
                <div style={{ marginTop:12 }}>
                  <label style={{ fontSize:12, color:T.textDim, display:"block", marginBottom:6 }}>
                    رابط المصدر (اختياري — يُضاف في نهاية المقال)
                  </label>
                  <input type="url" dir="ltr"
                    placeholder="https://..."
                    value={manualSourceUrl}
                    onChange={e=>setManualSourceUrl(e.target.value)}
                    style={{ ...css.input, width:"100%", boxSizing:"border-box" }} />
                </div>

                {/* ── Featured image picker ── */}
                <div style={{ marginTop:16, padding:14, background:T.surfaceAlt,
                  border:`1px dashed ${manualImage ? T.green : T.border}`, borderRadius:8 }}>
                  <div style={{ fontSize:12, color:T.textDim, marginBottom:10 }}>
                    الصورة البارزة (Featured Image) — اختياري
                  </div>
                  <div style={{ display:"flex", gap:12, alignItems:"center", flexWrap:"wrap" }}>
                    <label style={{ ...css.btn("ghost"), cursor:"pointer", fontSize:12 }}>
                      🖼️ {manualImage ? "تغيير الصورة" : "اختر صورة"}
                      <input type="file" accept="image/*" style={{ display:"none" }}
                        onChange={e => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          setManualImage(file);
                          const reader = new FileReader();
                          reader.onload = () => setManualImgPrev(reader.result);
                          reader.readAsDataURL(file);
                        }} />
                    </label>
                    {manualImage && (
                      <button style={{ ...css.btn("ghost"), fontSize:12, color:T.red }}
                        onClick={() => { setManualImage(null); setManualImgPrev(null); }}>
                        ✕ حذف
                      </button>
                    )}
                    {manualImage && (
                      <span style={{ fontSize:11, color:T.textDim }}>{manualImage.name}</span>
                    )}
                  </div>
                  {manualImgPrev && (
                    <img src={manualImgPrev} alt="preview"
                      style={{ marginTop:12, maxHeight:160, maxWidth:"100%",
                        borderRadius:6, border:`1px solid ${T.border}`, objectFit:"cover" }} />
                  )}
                </div>

                <div style={{ marginTop:12, display:"flex", gap:8 }}>
                  <button style={css.btn("primary")} onClick={processManual}
                    disabled={manualBusy||!manualContent.trim()}>
                    {manualBusy ? <><Spinner /> &nbsp;جارٍ المعالجة...</> : "⚡ معالجة وتحليل"}
                  </button>
                  <button style={css.btn("ghost")} onClick={() => {
                    setManualContent(""); setManualSourceUrl(""); setManualImage(null); setManualImgPrev(null);
                  }}>مسح</button>
                </div>
              </div>
            </div>
          )}

          {/* ════ LOG TAB ════ */}
          {tab==="log" && (
            <div>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
                <h2 style={{ fontSize:17, fontWeight:700, color:"#e0eaf5" }}>سجل العمليات</h2>
                <button style={css.btn("ghost")} onClick={()=>setLog([])}>مسح السجل</button>
              </div>
              <div style={{ ...css.card, fontFamily:"monospace", minHeight:300 }}>
                {log.length===0
                  ? <div style={{ color:T.textFaint, fontSize:12 }}>لا توجد سجلات بعد</div>
                  : [...log].reverse().map((l,i)=>(
                      <div key={i} style={css.logLine(l.type)}>
                        <span style={{ color:T.textFaint, marginLeft:10 }}>{l.time}</span>
                        {l.msg}
                      </div>
                    ))
                }
              </div>
            </div>
          )}

        </div>{/* /main */}
      </div>{/* /flex */}
    </div>
  );
}
